/**
 * DfpRequestBuilder - Builds DFP ad request URLs
 * @see https://support.google.com/dfp_premium/answer/1068325
 */
class DfpRequestBuilder {
    /**
     * Build a DFP ad request URL
     * @param {Object} config - Configuration object
     * @param {String} [config.url='https://pubads.g.doubleclick.net/gampad/live/ads']
     * @param {String} [config.sz='400x300|640x480|1920x1280']
     * @param {String} [config.iu='/2605/video_test/riot']
     * @param {String} [config.impl='s']
     * @param {Number} [config.gdfp_req=1]
     * @param {String} [config.env='vp']
     * @param {String} [config.output='vast']
     * @param {Number} [config.unviewed_position_start=1]
     * @param {String} [config.description_url='[description_url]']
     * @param {String} [config.correlator='[timestamp]']
     * @param {Number} [config.pmnd=0]
     * @param {Number} [config.pmxd=60000]
     * @param {Number} [config.pmad=-1]
     * @param {String} [config.vpos='preroll']
     * @param {Number} [config.pod=1]
     * @param {Number} [config.ad_rule=0]
     * @param {String} [config.asset='']
     * @param {String} [config.dfp.additionalParams={}] Optional. Merged if additional param properties exist. Additional property specific params.
     * @param {Object} clip - Media item
     * @param {Object} [clip.adConfig={}]
     * @param {String} [clip.adConfig.cust_params='']
     * @param {String} [clip.adConfig.vid=''] matchPk, contentId or gamePk based on media type
     * @param {Object} [cuePointVO] - Cue point value object
     * @returns {string} Complete DFP ad request URL
     */
    static build (config = {}, clip = {}, cuePointVO) {
        const adConfig = structuredClone(clip?.adConfig ?? {});
        const url = adConfig?.url ?? config?.url ?? 'https://pubads.g.doubleclick.net/gampad/live/ads';

        let mapper = {
            sz: config?.sz ?? '400x300|640x480|1920x1280',
            iu: config?.iu,
            impl: config?.impl ?? 's',
            gdfp_req: config?.gdfp_req ?? 1,
            env: config?.env ?? 'vp',
            output: config?.output ?? 'vast',
            unviewed_position_start: config?.unviewed_position_start ?? '1',
            url: window?.location?.href ?? '',
            description_url: config?.description_url ?? '[description_url]',
            correlator: config?.correlator ?? '[timestamp]',
            pmnd: config?.pmnd ?? '0',
            pmxd: config?.pmxd ?? '60000',
            pmad: config?.pmad ?? '1',
            vpos: config?.vpos ?? 'preroll',
            pp: config?.pp ?? 'web',
            pod: config?.pod ?? '1',
            ad_rule: config?.ad_rule ?? '1',
            asset: config?.asset ?? ''
        };

        if (config?.getDfpParams) {
            mapper = config.getDfpParams(config);
        }

        const additionalParams = config?.additionalParams ?? {};
        const event = cuePointVO?.event;

        // Add the `event` cuepoint data to the custom_params
        if (event !== undefined) {
            adConfig.cust_params = {
                ...(adConfig.cust_params ?? {}),
                evnt: event
            };
        }

        // Get dynamic custom params
        const custParams = DfpRequestBuilder.addCustomParams(adConfig, config);

        if (custParams !== undefined) {
            adConfig.cust_params = DfpRequestBuilder.encodeCustomParams(custParams);
        }

        // Merge adConfig params & additionalParams
        Object.assign(mapper, adConfig, additionalParams);

        // Use URLSearchParams for more robust query string creation
        const searchParams = new URLSearchParams();

        // Only add defined values to the search params
        Object.entries(mapper).forEach(([key, value]) => {
            if (value !== undefined) {
                searchParams.append(key, value);
            }
        });

        return `${url}?${searchParams.toString()}`;
    }

    /**
     * Encode custom parameters in DFP's expected format
     * @param {Object} params - Custom parameters object
     * @returns {string} Encoded parameters string
     */
    static encodeCustomParams (params = {}) {
        // Convert Object to encoded string using proper encoding
        return Object.entries(params)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => `${encodeURIComponent(key)}%3D${encodeURIComponent(value)}`)
            .join('%26');
    }

    /**
     * Add and merge custom parameters from various sources
     * @param {Object} adConfig - Ad configuration object
     * @param {Object} config - Main configuration object
     * @returns {Object} Merged parameters
     */
    static addCustomParams (adConfig = {}, config = {}) {
        const adConfigCustParams = adConfig?.cust_params ?? {};
        const configCustParams = config?.cust_params ?? {};

        // Merge parameters
        const params = {
            ...configCustParams,
            ...adConfigCustParams
        };

        // Add additional parameters
        return {
            ...params,
            topic: config?.topicId,
            width: config?.width,
            height: config?.height
        };
    }
}

export default DfpRequestBuilder;
